import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import "components/FilePreview/FilePreview.scss";
import { isImage, renderInIframe } from "components/FilePreview/utils";
import { downloadFile, getExtensionFromURL, getNameFromURL, getPreviewUrl } from "utils/common";

import type { Attachment } from "components/FileDropdown/components";
import type { Dispatch, SetStateAction } from "react";

enum KEY_CODES {
  LeftArrow = 37,
  RightArrow = 39
}

export interface PreviewFile extends Attachment {
  type?: string;
}

type FilePreviewProps = {
  previewIndex: number | null;
  files?: PreviewFile[];
  setPreviewIndex: Dispatch<SetStateAction<number | null>>;
};

export const FilePreview = ({ files, previewIndex, setPreviewIndex }: FilePreviewProps) => {
  const { t } = useTranslation();

  const filePreviewContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    filePreviewContentRef.current?.focus();
  }, [filePreviewContentRef.current]);

  if (!files?.length || previewIndex === null || previewIndex < 0 || previewIndex > files.length - 1) return null;

  const selectedFile = files[previewIndex];
  const name = selectedFile.name || getNameFromURL(selectedFile.url);

  const handleArrowClick = (orientation: KEY_CODES) => {
    setPreviewIndex(currentValue => {
      if (currentValue === null) return null;
      return orientation === KEY_CODES.RightArrow ? currentValue + 1 : currentValue - 1;
    });
  };

  const handlePreviewUsingKeyboard = (evt: { stopPropagation: () => void; keyCode: KEY_CODES }) => {
    evt.stopPropagation();
    const keyCode = evt.keyCode;

    if (![KEY_CODES.LeftArrow, KEY_CODES.RightArrow].includes(keyCode)) return;

    if (keyCode === KEY_CODES.LeftArrow && previewIndex > 0) setPreviewIndex(previewIndex - 1);
    else if (keyCode === KEY_CODES.RightArrow && previewIndex < files.length - 1) setPreviewIndex(previewIndex + 1);
  };

  const renderPreview = () => {
    const type = selectedFile.type || getExtensionFromURL(selectedFile.url);

    if (isImage(type, name)) return <img src={selectedFile.url} alt="preview" />;
    /**
     * key={selectedFile.url} is intentional — do not remove.
     *
     * Root cause: without a key, React reuses the iframe DOM node and only updates its src when the user flips between attachments.
     * Changing src on an existing iframe is a navigation, and every navigation of a loaded iframe pushes an entry onto the tab's browser history — silently inflating window.history.length without any page navigation.
     *
     * Impact: navigateBack in AppointmentDetails checks window.history.length > 1 before calling router.goBack().
     * The phantom iframe entries made goBack() consume a dead iframe entry instead of leaving the page — the component never unmounts and the Back button stays stuck in loading.
     *
     * Fix: the key gives each file its own iframe element. React destroys the old one and mounts a
     * fresh one per file, and an iframe's first load is history-neutral — no more phantom entries.
     */
    if (renderInIframe(type)) return <iframe key={selectedFile.url} src={getPreviewUrl(selectedFile.url)} title={selectedFile.url} width="100%" height="100%" />;

    return <h2>{t("v8_file_format_is_not_supported_for_preview", "File format is not supported for preview")}</h2>;
  };

  const handleOnDownloadClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await downloadFile(selectedFile.url);
  };

  return (
    <Modal open={true} closeOnDimmerClick={false} className="FilePreviewModal" centered tabIndex="0" onKeyDown={handlePreviewUsingKeyboard} openOnTriggerFocus>
      {files.length > 1 && (
        <>
          {previewIndex > 0 && <Icon className="chevron left left-arrow" onClick={() => handleArrowClick(KEY_CODES.LeftArrow)} />}
          {previewIndex < files.length - 1 && <Icon className="chevron right right-arrow" onClick={() => handleArrowClick(KEY_CODES.RightArrow)} />}
        </>
      )}

      <Modal.Header>
        <div className="FilePreviewHeader">
          <div>
            <div className="FilePreviewTitle">{t("v8_file_preview", "File preview")}</div>
            <div className="FilePreviewName">{name}</div>
          </div>

          <div className="Actions">
            <Button color="teal" onClick={handleOnDownloadClick}>
              {t("v8_download", "Download")}
            </Button>

            <Button color="green" onClick={() => setPreviewIndex(null)}>
              {t("v8_close", "Close")}
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Content>
        <div className="FilePreviewContent" tabIndex={0} ref={filePreviewContentRef}>
          {renderPreview()}
        </div>
      </Modal.Content>
    </Modal>
  );
};
